/*
 * WMD Option Dependencies Engine
 * - Generic dependency filtering for WMD products
 * - Supports multi-hop constraints (A->B, B->C, A->C)
 * - Supports combined constraints (intersection from multiple sources)
 * - Can optionally compute prices from price_modifiers
 */
(function (window, document) {
  'use strict';

  function normalizeKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[ä]/g, 'ae')
      .replace(/[ö]/g, 'oe')
      .replace(/[ü]/g, 'ue')
      .replace(/[ß]/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function uniq(arr) {
    var out = [];
    var seen = {};
    var i;
    for (i = 0; i < arr.length; i++) {
      var key = String(arr[i]);
      if (seen[key]) continue;
      seen[key] = true;
      out.push(arr[i]);
    }
    return out;
  }

  function intersectLists(a, b) {
    if (!Array.isArray(a) || !a.length) return Array.isArray(b) ? b.slice() : [];
    if (!Array.isArray(b) || !b.length) return [];

    var lookup = {};
    var i;
    for (i = 0; i < b.length; i++) lookup[String(b[i])] = true;

    var out = [];
    for (i = 0; i < a.length; i++) {
      var key = String(a[i]);
      if (lookup[key]) out.push(a[i]);
    }
    return uniq(out);
  }

  function parseDependencyKey(key) {
    var raw = String(key || '');
    var idx = raw.indexOf('_');
    if (idx < 1 || idx >= raw.length - 1) return null;
    return {
      source: raw.slice(0, idx),
      target: raw.slice(idx + 1)
    };
  }

  function WMDOptionDependencies(productHandle, dependencyConfig, options) {
    this.productHandle = productHandle || '';
    this.config = dependencyConfig || {};
    this.dependencies = (this.config && this.config.dependencies) || {};
    this.priceModifiers = (this.config && this.config.price_modifiers) || {};
    this.options = options || {};

    this.root = this.options.root || document;
    this.selector = this.options.selector || 'select[data-wmd-option]';
    this.selectsByOption = {};
    this.originalValuesByOption = {};
    this.rulesByTarget = {};
    this.isApplying = false;

    this.onDependenciesApplied = typeof this.options.onDependenciesApplied === 'function'
      ? this.options.onDependenciesApplied
      : null;
    this.onPriceUpdated = typeof this.options.onPriceUpdated === 'function'
      ? this.options.onPriceUpdated
      : null;

    this.init();
  }

  WMDOptionDependencies.prototype.init = function () {
    this.captureSelects();
    this.buildRulesByTarget();
    this.setupDependencyListeners();
    this.applyAllDependencies();
    this.updatePrices();
  };

  WMDOptionDependencies.prototype.captureSelects = function () {
    var selects = this.root.querySelectorAll(this.selector);
    var i;
    for (i = 0; i < selects.length; i++) {
      var select = selects[i];
      var optionName = select.getAttribute('data-wmd-option') || select.getAttribute('data-option') || '';
      if (!optionName) continue;

      this.selectsByOption[optionName] = select;
      this.originalValuesByOption[optionName] = [];

      var oi;
      for (oi = 0; oi < select.options.length; oi++) {
        var opt = select.options[oi];
        this.originalValuesByOption[optionName].push({
          value: opt.value,
          label: opt.getAttribute('data-label') || opt.textContent || ''
        });
      }
    }
  };

  WMDOptionDependencies.prototype.buildRulesByTarget = function () {
    var self = this;
    Object.keys(this.dependencies || {}).forEach(function (depKey) {
      var parsed = parseDependencyKey(depKey);
      if (!parsed) return;
      var source = parsed.source;
      var target = parsed.target;
      if (!self.rulesByTarget[target]) self.rulesByTarget[target] = [];
      self.rulesByTarget[target].push({
        source: source,
        target: target,
        mapping: self.dependencies[depKey] || {}
      });
    });
  };

  WMDOptionDependencies.prototype.setupDependencyListeners = function () {
    var self = this;
    var alreadyBound = {};

    Object.keys(this.dependencies || {}).forEach(function (depKey) {
      var parsed = parseDependencyKey(depKey);
      if (!parsed) return;
      var source = parsed.source;
      var sourceSelect = self.selectsByOption[source];
      if (!sourceSelect) return;

      if (alreadyBound[source]) return;
      alreadyBound[source] = true;

      sourceSelect.addEventListener('change', function () {
        if (self.isApplying) return;
        self.applyAllDependencies();
        self.updatePrices();
      });
    });

    Object.keys(this.selectsByOption).forEach(function (name) {
      if (alreadyBound[name]) return;
      var select = self.selectsByOption[name];
      if (!select) return;
      select.addEventListener('change', function () {
        if (self.isApplying) return;
        self.updatePrices();
      });
    });
  };

  WMDOptionDependencies.prototype.getSelectedLabel = function (optionName) {
    var select = this.selectsByOption[optionName];
    if (!select || !select.options || !select.options.length) return '';
    var option = select.options[select.selectedIndex];
    if (!option) return '';
    return option.getAttribute('data-label') || option.textContent || '';
  };

  WMDOptionDependencies.prototype.getAllowedValuesForTarget = function (targetOption) {
    var rules = this.rulesByTarget[targetOption] || [];
    var allowed = null;
    var i;

    for (i = 0; i < rules.length; i++) {
      var rule = rules[i];
      var sourceLabel = this.getSelectedLabel(rule.source);
      if (!sourceLabel) continue;
      var nextAllowed = rule.mapping[sourceLabel];
      if (!Array.isArray(nextAllowed)) continue;
      allowed = allowed === null ? nextAllowed.slice() : intersectLists(allowed, nextAllowed);
    }

    return allowed;
  };

  WMDOptionDependencies.prototype.filterOptions = function (sourceOption, targetOption, allowedValues) {
    var targetSelect = this.selectsByOption[targetOption];
    if (!targetSelect) return false;

    var allowAll = !Array.isArray(allowedValues);
    var allowedLookup = {};
    var i;

    if (!allowAll) {
      for (i = 0; i < allowedValues.length; i++) {
        allowedLookup[String(allowedValues[i])] = true;
      }
    }

    var firstAvailable = null;
    for (i = 0; i < targetSelect.options.length; i++) {
      var opt = targetSelect.options[i];
      var label = opt.getAttribute('data-label') || opt.textContent || '';
      var allowed = allowAll ? true : !!allowedLookup[String(label)];
      opt.disabled = !allowed;
      opt.style.display = allowed ? '' : 'none';
      if (allowed && firstAvailable === null) {
        firstAvailable = opt.value;
      }
    }

    var currentOpt = targetSelect.options[targetSelect.selectedIndex];
    var isCurrentAllowed = currentOpt && !currentOpt.disabled;
    if (!isCurrentAllowed && firstAvailable !== null) {
      targetSelect.value = firstAvailable;
      return true;
    }

    return false;
  };

  WMDOptionDependencies.prototype.applyAllDependencies = function () {
    this.isApplying = true;
    var changedTargets = [];
    var self = this;

    Object.keys(this.rulesByTarget).forEach(function (targetOption) {
      var allowedValues = self.getAllowedValuesForTarget(targetOption);
      var changed = self.filterOptions('', targetOption, allowedValues);
      if (changed) changedTargets.push(targetOption);
    });

    this.isApplying = false;

    var i;
    for (i = 0; i < changedTargets.length; i++) {
      var t = changedTargets[i];
      var select = this.selectsByOption[t];
      if (select) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (this.onDependenciesApplied) {
      this.onDependenciesApplied();
    }
  };

  WMDOptionDependencies.prototype.computeModifierSum = function () {
    var total = 0;
    var self = this;

    Object.keys(this.priceModifiers || {}).forEach(function (optionName) {
      var map = self.priceModifiers[optionName] || {};
      var selectedLabel = self.getSelectedLabel(optionName);
      var modifier = map[selectedLabel];
      if (typeof modifier === 'number') {
        total += modifier;
      } else if (modifier !== undefined && modifier !== null && modifier !== '') {
        var parsed = Number(modifier);
        if (!isNaN(parsed)) total += parsed;
      }
    });

    return total;
  };

  WMDOptionDependencies.prototype.updatePrices = function () {
    var modifierSum = this.computeModifierSum();
    if (this.onPriceUpdated) {
      this.onPriceUpdated(modifierSum);
      return;
    }

    var priceEl = document.getElementById('wmdFinalPrice');
    if (priceEl) {
      priceEl.setAttribute('data-wmd-modifier-sum', String(modifierSum));
    }
  };

  WMDOptionDependencies.prototype.getModifierFor = function (optionName, optionLabel) {
    var modifierMap = (this.priceModifiers && this.priceModifiers[optionName]) || {};
    if (modifierMap[optionLabel] === undefined || modifierMap[optionLabel] === null || modifierMap[optionLabel] === '') {
      return null;
    }
    var v = Number(modifierMap[optionLabel]);
    return isNaN(v) ? null : v;
  };

  WMDOptionDependencies.prototype.getNormalizedModifierFor = function (optionName, optionLabel) {
    var modifierMap = (this.priceModifiers && this.priceModifiers[optionName]) || {};
    var want = normalizeKey(optionLabel);
    var key;
    for (key in modifierMap) {
      if (!Object.prototype.hasOwnProperty.call(modifierMap, key)) continue;
      if (normalizeKey(key) === want) {
        var v = Number(modifierMap[key]);
        return isNaN(v) ? null : v;
      }
    }
    return null;
  };

  window.WMDOptionDependencies = WMDOptionDependencies;
})(window, document);
